/**
 * @krisspy-file
 * @type page
 * @name "CustomersList"
 * @title "Customers"
 * @description "View unique customers derived from bookings, with search, filter, and CSV export"
 * @routes ["/customers"]
 * @layout "DashboardLayout"
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Download,
  User,
  Loader2,
  AlertTriangle,
  Mail,
  Phone,
  Smartphone,
  MessageSquare,
  CheckCircle,
  XCircle,
  MinusCircle,
  X,
  Calendar,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { telHref, smsHref, whatsappHref } from '../utils/phone';
import { getCustomers, exportCustomersCsv, type CustomerRecord } from '../services/customers.service';
import { getMerchantById } from '../services/merchants.service';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency } from '../lib/currency';
import { useDateFormatter } from '../hooks/useDateFormatter';
import { useUrlFilters } from '../lib/useUrlFilters';

export default function CustomersList() {
  const navigate = useNavigate();
  const { merchantId } = useAuth();
  const { formatDateTime } = useDateFormatter();
  const { t } = useTranslation();
  const { getFilter, setFilter, clearFilters } = useUrlFilters({
    defaults: { search: '', consent: '' },
    storageKey: 'customers-filters',
  });

  const [customers, setCustomers] = useState<CustomerRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currencyCode, setCurrencyCode] = useState('EUR');
  const [exporting, setExporting] = useState(false);

  const searchQuery = getFilter('search');
  const consentFilter = getFilter('consent');

  // Load merchant currency
  useEffect(() => {
    async function loadMerchant() {
      if (!merchantId) return;
      const { data } = await getMerchantById(merchantId);
      if (data?.currency_code) setCurrencyCode(data.currency_code);
    }
    loadMerchant();
  }, [merchantId]);

  // Load customers
  useEffect(() => {
    async function load() {
      if (!merchantId) return;
      setLoading(true);
      setError(null);

      const marketingConsent =
        consentFilter === 'yes' ? true : consentFilter === 'no' ? false : null;

      const { data, error: fetchError } = await getCustomers(merchantId, {
        searchQuery: searchQuery || undefined,
        marketingConsent,
        limit: 500,
      });

      if (fetchError) {
        setError(fetchError);
      } else {
        setCustomers(data || []);
      }
      setLoading(false);
    }
    load();
  }, [merchantId, searchQuery, consentFilter]);

  const handleExportCsv = () => {
    if (customers.length === 0) return;
    setExporting(true);
    try {
      const csv = exportCustomersCsv(customers, currencyCode);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `customers-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const hasActiveFilters = searchQuery || consentFilter;

  return (
    <div className="space-y-lg">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-md">
        <div>
          <h1 className="text-2xl font-bold text-primary">{t('customers.title')}</h1>
          <p className="text-sm text-secondary mt-xs">
            {loading ? t('common.loading') : t('customers.customerCount', { count: customers.length })}
          </p>
        </div>
        <button
          onClick={handleExportCsv}
          disabled={exporting || customers.length === 0}
          className="btn btn-secondary flex items-center gap-sm"
        >
          <Download className="w-4 h-4" />
          {exporting ? t('customers.exporting') : t('customers.exportCsv')}
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-md">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setFilter('search', e.target.value)}
            placeholder={t('customers.searchPlaceholder')}
            className="input pl-10 w-full"
          />
        </div>
        <div className="flex-shrink-0">
          <select
            value={consentFilter}
            onChange={(e) => setFilter('consent', e.target.value)}
            className="input min-w-[180px]"
          >
            <option value="">{t('customers.allConsent')}</option>
            <option value="yes">{t('customers.optedIn')}</option>
            <option value="no">{t('customers.notOptedIn')}</option>
          </select>
        </div>
        {hasActiveFilters && (
          <button
            onClick={() => clearFilters()}
            className="btn btn-secondary flex items-center gap-sm"
          >
            <X className="w-4 h-4" />
            {t('customers.clear')}
          </button>
        )}
      </div>

      {/* Error state */}
      {error && (
        <div className="flex items-center gap-md p-md bg-danger/10 text-danger rounded-lg">
          <AlertTriangle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="flex items-center justify-center py-xl">
          <Loader2 className="w-8 h-8 text-blue animate-spin" />
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && customers.length === 0 && (
        <div className="text-center py-xl">
          <User className="w-12 h-12 text-secondary mx-auto mb-md" />
          <h3 className="text-lg font-medium text-primary mb-xs">{t('customers.noCustomers')}</h3>
          <p className="text-secondary">
            {hasActiveFilters
              ? t('customers.tryAdjusting')
              : t('customers.willAppear')}
          </p>
        </div>
      )}

      {/* Table */}
      {!loading && !error && customers.length > 0 && (
        <div className="overflow-x-auto border border-border rounded-lg">
          <table className="w-full">
            <thead>
              <tr className="bg-secondary">
                <th className="text-left px-lg py-md text-xs font-semibold text-secondary uppercase tracking-wider">
                  {t('customers.customer')}
                </th>
                <th className="text-left px-lg py-md text-xs font-semibold text-secondary uppercase tracking-wider hidden md:table-cell">
                  {t('customers.contact')}
                </th>
                <th className="text-center px-lg py-md text-xs font-semibold text-secondary uppercase tracking-wider hidden lg:table-cell">
                  {t('customers.bookings')}
                </th>
                <th className="text-right px-lg py-md text-xs font-semibold text-secondary uppercase tracking-wider hidden lg:table-cell">
                  {t('customers.totalSpent')}
                </th>
                <th className="text-center px-lg py-md text-xs font-semibold text-secondary uppercase tracking-wider hidden sm:table-cell">
                  {t('customers.marketing')}
                </th>
                <th className="text-left px-lg py-md text-xs font-semibold text-secondary uppercase tracking-wider hidden xl:table-cell">
                  {t('customers.lastBooking')}
                </th>
                <th className="px-lg py-md text-xs font-semibold text-secondary uppercase tracking-wider text-right">
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {customers.map((customer, idx) => (
                <tr
                  key={`${customer.customer_email}-${idx}`}
                  className="hover:bg-secondary/5 transition-colors"
                >
                  {/* Customer Name */}
                  <td className="px-lg py-md">
                    <div className="flex items-center gap-md">
                      <div className="w-8 h-8 rounded-full bg-blue/10 flex items-center justify-center flex-shrink-0">
                        <User className="w-4 h-4 text-blue" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-primary truncate">
                          {customer.customer_firstname} {customer.customer_lastname}
                        </div>
                        <div className="text-sm text-secondary truncate md:hidden">
                          {customer.customer_email}
                        </div>
                      </div>
                      {(() => {
                        const callHref = telHref(customer.customer_phone);
                        const smsLink = smsHref(customer.customer_phone);
                        const waLink = whatsappHref(customer.customer_phone);
                        if (!callHref && !smsLink && !waLink) return null;
                        const round =
                          'flex-shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full transition-colors';
                        return (
                          <div className="md:hidden flex items-center gap-xs">
                            {callHref && (
                              <a
                                href={callHref}
                                title={t('customers.callCustomer')}
                                aria-label={t('customers.callCustomer')}
                                className={`${round} bg-blue/10 text-blue hover:bg-blue/20`}
                              >
                                <Phone className="w-4 h-4" />
                              </a>
                            )}
                            {smsLink && (
                              <a
                                href={smsLink}
                                title={t('customers.smsCustomer')}
                                aria-label={t('customers.smsCustomer')}
                                className={`${round} bg-blue/10 text-blue hover:bg-blue/20`}
                              >
                                <Smartphone className="w-4 h-4" />
                              </a>
                            )}
                            {waLink && (
                              <a
                                href={waLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={t('customers.whatsappCustomer')}
                                aria-label={t('customers.whatsappCustomer')}
                                className={`${round} bg-success/10 text-success hover:bg-success/20`}
                              >
                                <MessageSquare className="w-4 h-4" />
                              </a>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </td>

                  {/* Contact */}
                  <td className="px-lg py-md hidden md:table-cell">
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-xs text-sm text-primary">
                        <Mail className="w-3.5 h-3.5 text-secondary flex-shrink-0" />
                        <span className="truncate max-w-[200px]">{customer.customer_email}</span>
                      </div>
                      {(() => {
                        const callHref = telHref(customer.customer_phone);
                        const smsLink = smsHref(customer.customer_phone);
                        const waLink = whatsappHref(customer.customer_phone);
                        const content = (
                          <>
                            <Phone className="w-3.5 h-3.5 flex-shrink-0" />
                            <span>{customer.customer_phone}</span>
                          </>
                        );
                        const phoneNode = callHref ? (
                          <a
                            href={callHref}
                            className="flex items-center gap-xs text-sm text-secondary hover:text-blue transition-colors"
                            title={t('customers.callCustomer')}
                            aria-label={t('customers.callCustomer')}
                          >
                            {content}
                          </a>
                        ) : (
                          <div className="flex items-center gap-xs text-sm text-secondary">{content}</div>
                        );
                        if (!smsLink && !waLink) return phoneNode;
                        return (
                          <div className="flex items-center gap-sm">
                            {phoneNode}
                            {smsLink && (
                              <a
                                href={smsLink}
                                title={t('customers.smsCustomer')}
                                aria-label={t('customers.smsCustomer')}
                                className="text-secondary hover:text-blue transition-colors"
                              >
                                <Smartphone className="w-3.5 h-3.5" />
                              </a>
                            )}
                            {waLink && (
                              <a
                                href={waLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={t('customers.whatsappCustomer')}
                                aria-label={t('customers.whatsappCustomer')}
                                className="text-secondary hover:text-success transition-colors"
                              >
                                <MessageSquare className="w-3.5 h-3.5" />
                              </a>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </td>

                  {/* Bookings count */}
                  <td className="px-lg py-md text-center hidden lg:table-cell">
                    <span className="inline-flex items-center justify-center min-w-[2rem] px-sm py-0.5 rounded-full bg-blue/10 text-blue text-sm font-medium">
                      {customer.total_bookings}
                    </span>
                  </td>

                  {/* Total Spent */}
                  <td className="px-lg py-md text-right hidden lg:table-cell">
                    <span className="font-medium text-primary">
                      {formatCurrency(customer.total_spent || 0, currencyCode)}
                    </span>
                  </td>

                  {/* Marketing Consent */}
                  <td className="px-lg py-md text-center hidden sm:table-cell">
                    {customer.marketing_consent === null ? (
                      <span className="inline-flex items-center gap-xs text-sm text-secondary">
                        <MinusCircle className="w-4 h-4" />
                        <span className="hidden lg:inline">{t('common.na')}</span>
                      </span>
                    ) : customer.marketing_consent ? (
                      <span className="inline-flex items-center gap-xs text-sm text-success">
                        <CheckCircle className="w-4 h-4" />
                        <span className="hidden lg:inline">{t('customers.optedIn')}</span>
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-xs text-sm text-error">
                        <XCircle className="w-4 h-4" />
                        <span className="hidden lg:inline">{t('customers.declined')}</span>
                      </span>
                    )}
                  </td>

                  {/* Last Booking */}
                  <td className="px-lg py-md hidden xl:table-cell">
                    <span className="text-sm text-secondary">
                      {customer.last_booking_at ? formatDateTime(customer.last_booking_at) : t('common.na')}
                    </span>
                  </td>

                  {/* View Bookings */}
                  <td className="px-lg py-md text-right">
                    <button
                      onClick={() => navigate(`/bookings?search=${encodeURIComponent(customer.customer_email)}&category=all`)}
                      className="inline-flex items-center gap-xs text-sm text-blue hover:text-blue/80 transition-colors"
                      title={`View all bookings for ${customer.customer_email}`}
                    >
                      <Calendar className="w-4 h-4" />
                      <span className="hidden sm:inline">{t('customers.bookings')}</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
